import dynamic from 'next/dynamic';
import styled from 'styled-components';
// import { Trans } from 'react-i18next';

import { FeatureFlags } from 'featureFlags/constants';
import { getImageType } from 'shared/utils/gridEventTrackingUtils';
import { getNameAlignment } from 'search/utils/getNameAlignment';
import { ICollection } from 'search/types/Collection';
import { LatestSearches } from 'search/components/search/LatestSearches';
import { Loader } from 'shared/components/Loader';
import { navigationThemes } from 'styles/theme/navigation';
import { partialMatch } from 'search/utils/stringMatchUtils';
import { SearchResultImageGrid } from 'search/components/search/SearchResultImageGrid';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { t } from 'localization/utils/t';
import { useConstructorClient } from 'shared/hooks/useConstructorClient';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useStoreSelector } from 'store/hooks/storeHooks';
import type { IConstructorResult } from 'shared/types/ConstructorResult';

const TP = 'search.components.search.SearchResult';

const SearchTermSearchSuggestions = dynamic(() => import('./SearchTermSearchSuggestions'));

interface ISearchResults {
  collectionResults?: Array<ICollection>;
  error: Error;
  isLoading?: boolean;
  resultId: string;
  productResults: Array<IConstructorResult>;
  searchTerm: string;
}

const SearchResults = ({
  collectionResults,
  error,
  isLoading,
  resultId,
  productResults,
  searchTerm,
}: ISearchResults) => {
  const isLatestSearchesEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_LATEST_SEARCHES);
  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const ConstructorClient = useConstructorClient();
  const eventTrackingEventContext = useEventContext();
  const { user } = useStoreSelector((state) => state.auth);

  const isPartialGiftCardsSearchTerm = /\s/g.test(searchTerm)
    ? partialMatch('gift cards', searchTerm)
    : partialMatch('giftcards', searchTerm);

  const handleProductClick = (result, index: number) => {
    const { data, value } = result;

    sendTrackingEvent('CONSTRUCTOR_GRID_CELL_TAP', {
      event_context: eventTrackingEventContext,
      is_filtered: false,
      is_preview: true,
      object_id: data.id,
      pdp_image_type: getImageType(data.image_url, data?.cheapest_product),
      position: (index + 1).toString(),
      product_template_slug: data.slug,
      result_id: resultId,
      search_string: searchTerm,
      service_name: isNewSearchPageEnabled ? 'consumer_search' : 'constructor',
      ...(user && { userID: user.id }),
    });

    if (!isNewSearchPageEnabled) {
      ConstructorClient?.tracker?.trackAutocompleteSelect(value, {
        original_query: searchTerm,
        section: 'Products',
      });
    }
  };

  const handleCollectionClick = (index: number) => {
    if (!isNewSearchPageEnabled && collectionResults.length > 0) {
      const collection = collectionResults.at(index);
      ConstructorClient?.tracker?.trackAutocompleteSelect(collection.value, {
        original_query: searchTerm,
        section: 'Collections',
      });
    }
  };

  // if (error) {
  //   return (
  //     <SearchResults.Wrapper>
  //       <Trans
  //         i18nKey={`${TP}.temporarilyUnavailable`}
  //         defaults="This page is temporarily unavailable. Please try your search again or <0>return home</0>."
  //         components={[<SearchResults.Link data-qa="search_box_constructor_error" href="/" />]}
  //       />
  //     </SearchResults.Wrapper>
  //   );
  // }

  if (isLoading) {
    return (
      <SearchResults.Wrapper>
        <Loader />
      </SearchResults.Wrapper>
    );
  }

  if (error || (productResults.length === 0 && collectionResults.length === 0)) {
    return (
      <SearchResults.NoResultsWrapper>
        {isPartialGiftCardsSearchTerm ? (
          <SearchTermSearchSuggestions
            collectionResults={collectionResults}
            searchTerm={searchTerm}
            handleOnClick={handleCollectionClick}
          />
        ) : (
          <SearchResults.NoResults data-qa="no_search_results">
            {t(`${TP}.noResult`, 'No results were found for {searchTerm}', {
              searchTerm,
            })}
          </SearchResults.NoResults>
        )}
        {isLatestSearchesEnabled && <LatestSearches />}
      </SearchResults.NoResultsWrapper>
    );
  }

  return (
    <SearchResults.Wrapper>
      <SearchTermSearchSuggestions
        collectionResults={collectionResults}
        searchTerm={searchTerm}
        handleOnClick={handleCollectionClick}
      />
      {productResults.length > 0 && (
        <SearchResults.GridWrapper data-qa="search_results_grid">
          {productResults.map((result, index) => {
            return (
              <SearchResultImageGrid
                align={getNameAlignment(index, 5)}
                imageSize="20vw"
                key={result?.data?.id || result?.data?.slug}
                onClick={() => handleProductClick(result, index)}
                product={result as { data: Record<string, any>; value: string }}
                missingAssetPath={navigationThemes.light.missingAssetPath}
                showPtNames
              />
            );
          })}
        </SearchResults.GridWrapper>
      )}
    </SearchResults.Wrapper>
  );
};

SearchResults.Wrapper = styled.div`
  padding: 20px;
`;

SearchResults.GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-gap: 5px;
`;

SearchResults.LoaderWrapper = styled.div`
  margin: 16px;
`;

SearchResults.NoResultsWrapper = styled.div`
  padding: 16px;
`;

SearchResults.NoResults = styled.div`
  margin-bottom: 24px;
`;

SearchResults.Link = styled.a`
  text-decoration: underline;

  &:hover {
    text-decoration: underline;
  }
`;

export default SearchResults;
